// src/pages/PublicSeriesPage.js
// PUBLIC (signed-out) view of ONE challenge.
//
// Reached at the SAME address members use — /workouts?series=<slug-or-id>.
// App.js's WorkoutsRoute decides which surface that URL serves: a signed-out
// visitor whose token resolves to a PUBLISHED + PUBLIC challenge lands here;
// every other case falls through to the normal ClerkProtected Workouts page.
// The owner asked to reuse the existing links rather than mint a second address
// (links are already in circulation). See docs/decisions/adr-public-series-access.md.
//
// Three things this surface deliberately does NOT do:
//   1. It never renders Workouts. That component owns the live-class path and is
//      ~6,700 lines; putting an anonymous code path through it is exactly the
//      blast radius this design avoids.
//   2. It writes NOTHING. VideoPlayerModal is already fully guarded on !userId
//      (every completion write, the auto-complete effects, the track-watch
//      heartbeat and the "Log this workout" control early-return without a
//      user), so passing userId={null} gives us "no tracking, no Mark done"
//      with zero changes to the player.
//   3. It shows no member furniture — no AppNav, no tabs, no admin affordances.
//
// The challenge content itself renders through the SAME components as the
// in-app view (src/components/SeriesContent.js), so the two can't drift.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SignUpButton, SignInButton } from '@clerk/clerk-react';
import VideoPlayerModal from '../components/VideoPlayerModal';
import FormattedText from '../components/FormattedText';
import { SeriesMetaPills, SeriesInfoBlocks } from '../components/SeriesContent';
import { fetchSiteContent, getCachedSiteContent } from '../services/firebase';
import { fetchVideosFromCollection } from '../services/bunnyStream';
import { fetchProgramVideos, shapeProgramVideo } from '../services/programVideos';
import {
    fetchSeries, findSeriesByToken, orderSeriesVideos, seriesDayLabel,
    isSeriesPublic, isSeriesDayReleased, seriesDayReleaseAt, formatSeriesReleaseDate,
    indexSeriesDays, seriesDayCount,
} from '../services/series';
import { serverNow, syncServerClock } from '../services/serverClock';

// Copy resolver — every user-facing string is admin-editable with a fallback.
const COPY = {
    seriesPublicInviteHeading: 'Want to keep this going?',
    seriesPublicInviteClosingLine: 'Create a free account and your days here start counting toward your streak.',
    seriesPublicCreateAccountLabel: 'Create free account',
    seriesPublicSignInLabel: 'Sign in',
    seriesPublicSignInPrefix: 'Already have one?',
    seriesPublicStartHereLabel: 'Start here',
};
function copyFrom(content, key) {
    const v = content && content[key];
    return (typeof v === 'string' && v.trim()) ? v : COPY[key];
}

// Muted tone. NOTE: production sets --mom-warm-gray identical to --mom-charcoal,
// so styling secondary text the conventional way renders it at full heading
// strength. Derive from charcoal toward cream instead. (See the welcome-modal
// note in docs/ux/welcome-modal.md — same trap.)
const MUTED = 'color-mix(in srgb, var(--mom-charcoal) 62%, var(--mom-cream))';

// ══════════════════════════════════════════════════════════════════════════
//  Presentational pieces — module scope (never nested, per no-inner-components)
// ══════════════════════════════════════════════════════════════════════════

function PageShell({ children }) {
    return (
        <div style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <div className="mom-atmosphere" />
            <div style={{ maxWidth: 800, margin: '0 auto', padding: 'clamp(20px, 4vw, 44px) clamp(14px, 4vw, 28px)' }}>
                {/* The only chrome a stranger gets: who this is, and a quiet way
                    back in for a member who happens to be logged out. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 26 }}>
                    <a href="/" style={{ textDecoration: 'none' }}>
                        <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 19, color: 'var(--mom-charcoal)' }}>
                            Moments of Movement
                        </span>
                    </a>
                    <SignInButton mode="modal">
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, fontWeight: 600, color: MUTED, padding: 0, flexShrink: 0 }}>
                            Sign in
                        </button>
                    </SignInButton>
                </div>
                {children}
            </div>
        </div>
    );
}

// Paint-first placeholder. Inline shimmer, NOT the mom-skeleton class — that is
// invisible on cream. This is an acquisition surface arriving from a cold social
// tap, so it must never show a bare spinner or flash "not found" mid-resolve.
function ShimmerBlock({ height, radius = 16, width = '100%', style }) {
    return (
        <div style={{
            width, height, borderRadius: radius,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.5) 25%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.5) 75%)',
            backgroundSize: '200% 100%', animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }} />
    );
}

function LoadingChallenge() {
    return (
        <div>
            <div style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 24, overflow: 'hidden', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                <ShimmerBlock height={200} radius={0} />
                <div style={{ padding: '20px 24px 24px' }}>
                    <ShimmerBlock height={28} width="65%" radius={8} style={{ marginBottom: 14 }} />
                    <ShimmerBlock height={15} width="90%" radius={6} style={{ marginBottom: 8 }} />
                    <ShimmerBlock height={15} width="75%" radius={6} />
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[0, 1, 2, 3].map((i) => <ShimmerBlock key={i} height={68} />)}
            </div>
        </div>
    );
}

// The invitation. Said ONCE, after the last day (2026-09-19 — it used to also
// sit above the day list, which put the ask in front of the thing they came
// for). It is an INVITATION, never remediation — nothing here is withheld from
// them, so the honest pitch is memory (streak/progress), not access. Never
// "unlock", never "sign up to continue".
function JoinInvitation({ content }) {
    return (
        <div style={{
            marginTop: 28,
            padding: 'clamp(22px,3vw,30px)',
            borderRadius: 22,
            background: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(20px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            border: '1px solid rgba(255,255,255,0.55)',
            boxShadow: 'var(--shadow-md)',
            textAlign: 'center',
        }}>
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 'clamp(19px,3vw,24px)', color: 'var(--mom-charcoal)', marginBottom: 8 }}>
                {copyFrom(content, 'seriesPublicInviteHeading')}
            </h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: MUTED, lineHeight: 1.6, marginBottom: 20 }}>
                {copyFrom(content, 'seriesPublicInviteClosingLine')}
            </p>
            <SignUpButton mode="modal">
                <button className="mom-btn mom-btn-primary" style={{ padding: '14px 30px', borderRadius: 13, fontSize: 15.5, fontWeight: 700 }}>
                    {copyFrom(content, 'seriesPublicCreateAccountLabel')}
                </button>
            </SignUpButton>
            <div style={{ marginTop: 14, fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: MUTED }}>
                {copyFrom(content, 'seriesPublicSignInPrefix')}{' '}
                <SignInButton mode="modal">
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, fontWeight: 600, color: 'var(--mom-link, var(--mom-primary-dark))', padding: 0 }}>
                        {copyFrom(content, 'seriesPublicSignInLabel')}
                    </button>
                </SignInButton>
            </div>
        </div>
    );
}

// One day row. Mirrors the in-app row minus every account-bound affordance:
// no ✓, no completion count, no "Mark done". Scheduled days read IDENTICALLY to
// the member view, because a drip challenge is exactly the kind of thing that
// gets shared before it starts — someone arriving early must understand they're
// early, not think the page is broken.
function PublicDayRow({ video, isDay, dayNumber, kicker, dayTemplate, label, locked, unlockNote, startHere, onPlay, showLength = true }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
            background: 'var(--mom-white)', borderRadius: 16,
            boxShadow: locked ? 'none' : 'var(--shadow-sm)',
            border: startHere ? '1.5px solid color-mix(in srgb, var(--mom-primary) 45%, transparent)' : '1px solid var(--mom-cream-dark)',
            opacity: locked ? 0.7 : 1,
        }}>
            <div
                onClick={locked ? undefined : onPlay}
                role={locked ? undefined : 'button'}
                tabIndex={locked ? undefined : 0}
                style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, cursor: locked ? 'default' : 'pointer' }}
            >
                <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mom-cream)', color: MUTED, fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 16, fontWeight: 700 }}>
                    {locked ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    ) : isDay ? dayNumber : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20" /></svg>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {(() => {
                        const lead = isDay ? dayTemplate.replace('{n}', dayNumber) : kicker;
                        const text = [lead, startHere].filter(Boolean).join(' · ');
                        if (!text) return null;
                        return (
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--mom-primary)', textTransform: 'uppercase' }}>{text}</p>
                        );
                    })()}
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: locked ? MUTED : 'var(--mom-charcoal)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}{video.length && showLength ? <span style={{ fontWeight: 400, color: MUTED }}> · {video.length} min</span> : null}
                    </p>
                    {locked && unlockNote && (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'color-mix(in srgb, var(--mom-primary) 70%, var(--mom-charcoal))', marginTop: 2 }}>
                            {unlockNote}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════
//  Main page
// ══════════════════════════════════════════════════════════════════════════

// `token` is the ?series= value. `onNotPublic` is called when the token does NOT
// resolve to a published public challenge — the route then falls back to the
// normal ClerkProtected Workouts path, which stashes the destination, sends the
// visitor to sign in, and returns them here afterwards. That is TODAY's exact
// behaviour for a private challenge link, preserved.
function PublicSeriesPage({ token, onNotPublic }) {
    // Paint-first: start from the localStorage site-content cache so the shell
    // renders with the right theme/copy on frame zero, then refresh in place.
    const [content, setContent] = useState(() => getCachedSiteContent() || {});
    const [series, setSeries] = useState(null);
    const [videos, setVideos] = useState([]);
    const [resolving, setResolving] = useState(true);
    const [videosLoading, setVideosLoading] = useState(true);
    const [activeVideo, setActiveVideo] = useState(null);
    // Couldn't reach the data at all (distinct from "no such challenge").
    const [loadFailed, setLoadFailed] = useState(false);
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        fetchSiteContent().then((c) => { if (!cancelled && c) setContent(c); }).catch(() => {});
        // Release-date gating reads the server-synced clock, so a device with a
        // fast clock can't open a scheduled day early.
        syncServerClock();
        return () => { cancelled = true; };
    }, []);

    // Resolve the token → a public challenge, or hand back to the auth path.
    //
    // fetchSeries returns null for "couldn't load" and [] for "loaded, empty" —
    // a distinction that MATTERS here. Collapsing them would send a stranger to
    // a sign-in wall on a passing network blip, which is exactly the PROGRAM-001
    // failure mode (a transient fetch failure rendering as a permanent state) on
    // the one surface where that costs us the visitor. So: retry once, and if it
    // still won't load, say so and offer a retry rather than mislabelling them.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadFailed(false);
            let list = await fetchSeries();
            if (cancelled) return;
            if (list === null) {
                await new Promise((r) => setTimeout(r, 600));
                if (cancelled) return;
                list = await fetchSeries();
                if (cancelled) return;
            }
            if (list === null) {
                setResolving(false);
                setLoadFailed(true);
                return;
            }
            const match = findSeriesByToken(list, token);
            // Fail closed: not found, not published, or not flagged public all
            // take the same road as a private link does today.
            if (!match || !isSeriesPublic(match)) {
                setResolving(false);
                if (typeof onNotPublic === 'function') onNotPublic();
                return;
            }
            setSeries(match);
            setResolving(false);
        })();
        return () => { cancelled = true; };
    }, [token, onNotPublic, retryKey]);

    // Day videos: Bunny collection + in-app recordings (programVideos scoped by
    // seriesId), merged and ordered exactly as the member view does.
    useEffect(() => {
        if (!series) return;
        let cancelled = false;
        setVideosLoading(true);
        (async () => {
            const [vids, recorded] = await Promise.all([
                series.bunnyCollectionId ? fetchVideosFromCollection(series.bunnyCollectionId) : Promise.resolve([]),
                fetchProgramVideos(series.id),
            ]);
            if (cancelled) return;
            const recordedVideos = (recorded || [])
                .filter((pv) => pv.status === 'ready' && pv.muxPlaybackId)
                .map(shapeProgramVideo);
            setVideos(orderSeriesVideos([...(vids || []), ...recordedVideos], series.videoOrder));
            setVideosLoading(false);
        })();
        return () => { cancelled = true; };
    }, [series]);

    const dayTemplate = content.seriesDayLabelTemplate || 'Day {n}';

    // Release state. No admin here by definition, so every scheduled day gates.
    const releaseNow = serverNow();
    const isLocked = useCallback(
        (v) => !isSeriesDayReleased(series?.releaseDates, v.id, releaseNow),
        [series, releaseNow],
    );

    const heroPills = useMemo(() => {
        if (!series) return [];
        // Days only — a welcome note shouldn't make a 7-day challenge read as 8.
        const dayCount = seriesDayCount(videos, series.nonDayVideos) || (series.videoOrder || []).length;
        // Same opt-out as the member view — a shared link must not contradict
        // what a member sees in the app. Absent ⇒ lengths show as before.
        const totalMinutes = videos.reduce((sum, v) => sum + (Number(v.length) || 0), 0);
        const lengthPill = series.hideVideoLengths === true ? null : (totalMinutes >= 60
            ? `${Math.floor(totalMinutes / 60)} hr${totalMinutes % 60 ? ` ${totalMinutes % 60} min` : ''} total`
            : (totalMinutes > 0 ? `${totalMinutes} min total` : null));
        return [
            dayCount > 0 ? `${dayCount} day${dayCount === 1 ? '' : 's'}` : null,
            lengthPill,
            ...((series.metaPills) || []),
        ].filter(Boolean);
    }, [series, videos]);

    if (loadFailed) {
        return (
            <PageShell>
                <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: MUTED, lineHeight: 1.6, marginBottom: 18 }}>
                        We couldn't load this challenge just now. It's most likely a connection hiccup.
                    </p>
                    <button className="mom-btn mom-btn-primary"
                        onClick={() => { setResolving(true); setRetryKey((k) => k + 1); }}
                        style={{ padding: '12px 26px', borderRadius: 13, fontSize: 14.5, fontWeight: 700 }}>
                        Try again
                    </button>
                </div>
            </PageShell>
        );
    }

    if (resolving || !series) {
        return <PageShell><LoadingChallenge /></PageShell>;
    }

    const hasInfo = ((series.highlights || []).some((h) => h && String(h).trim()))
        || ((series.sections || []).some((s) => s && ((s.heading || '').trim() || (s.body || '').trim())))
        || ((series.faqs || []).some((f) => f && (f.question || '').trim()));
    const infoBlocks = hasInfo ? <SeriesInfoBlocks series={series} siteConfig={content} /> : null;

    // "Start here" marks the first day they can actually do — the public
    // equivalent of "Up next", with no completion knowledge behind it.
    const startHereIdx = videos.findIndex((v) => !isLocked(v));
    const dayIndex = indexSeriesDays(videos, series.nonDayVideos);

    return (
        <PageShell>
            {/* Hero */}
            <div style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 24, overflow: 'hidden', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                {series.imageUrl && <img src={series.imageUrl} alt="" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />}
                <div style={{ padding: '20px 24px 24px' }}>
                    <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(24px,4.5vw,30px)', fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8, lineHeight: 1.2 }}>{series.title}</h1>
                    {series.tagline && series.tagline.trim() && (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'color-mix(in srgb, var(--mom-primary) 78%, var(--mom-charcoal))', marginBottom: 10, lineHeight: 1.45 }}>{series.tagline}</p>
                    )}
                    {series.description && (
                        <div style={{ marginBottom: 16, fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
                            <FormattedText text={series.description} />
                        </div>
                    )}
                    <SeriesMetaPills pills={heroPills} />
                </div>
            </div>

            {hasInfo && series.detailsFirst ? <div style={{ marginBottom: 8 }}>{infoBlocks}</div> : null}

            {series.daysHeading && series.daysHeading.trim() && (
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 19, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 12 }}>{series.daysHeading}</h2>
            )}

            {videosLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[0, 1, 2].map((i) => <ShimmerBlock key={i} height={68} />)}
                </div>
            ) : videos.length === 0 ? (
                <p style={{ textAlign: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: MUTED, padding: 24 }}>
                    This challenge is being put together — check back shortly.
                </p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {videos.map((v, i) => {
                        const locked = isLocked(v);
                        const row = dayIndex[i] || { isDay: true, dayNumber: i + 1, kicker: '' };
                        const releaseAt = seriesDayReleaseAt(series.releaseDates, v.id);
                        return (
                            <PublicDayRow
                                key={v.id}
                                video={v}
                                isDay={row.isDay}
                                dayNumber={row.dayNumber}
                                kicker={row.kicker}
                                dayTemplate={dayTemplate}
                                label={seriesDayLabel({ video: v, dayLabels: series.dayLabels, dayNumber: row.dayNumber || (i + 1), template: dayTemplate })}
                                locked={locked}
                                unlockNote={releaseAt ? `${content.seriesLockedLabel || 'Unlocks'} ${formatSeriesReleaseDate(releaseAt)}` : ''}
                                startHere={i === startHereIdx ? copyFrom(content, 'seriesPublicStartHereLabel') : ''}
                                showLength={series.hideVideoLengths !== true}
                                onPlay={() => setActiveVideo(v)}
                            />
                        );
                    })}
                </div>
            )}

            {hasInfo && !series.detailsFirst ? <div style={{ marginTop: 24 }}>{infoBlocks}</div> : null}

            <JoinInvitation content={content} />

            {/* The member player, reused as-is. userId={null} is what makes this
                read-only: every completion write and the watch-time heartbeat
                inside the modal early-return without a user, and the manual
                "Log this workout" control is gated on userId. No new player, no
                new completion path — so this surface cannot touch COMPLETION-*. */}
            {activeVideo && (
                <VideoPlayerModal
                    video={activeVideo}
                    onClose={() => setActiveVideo(null)}
                    userId={null}
                    isAdmin={false}
                    autoplay
                    showLength={series.hideVideoLengths !== true}
                />
            )}
        </PageShell>
    );
}

export default PublicSeriesPage;
